import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calculator, Download, DollarSign, Clock, FileText, Users, CheckCircle2, AlertCircle, X, Loader2, Archive } from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Payroll() {
  const [payrolls, setPayrolls] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [craRemittance, setCraRemittance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [activeTab, setActiveTab] = useState('paystubs');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [showRunPayrollModal, setShowRunPayrollModal] = useState(false);
  const [showROEModal, setShowROEModal] = useState(false);
  const [showTaxFormModal, setShowTaxFormModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [taxFormYear, setTaxFormYear] = useState(new Date().getFullYear());

  const [runningPayroll, setRunningPayroll] = useState(false);

  useEffect(() => { fetchReferenceData(); }, []);

  useEffect(() => { fetchPayrolls(); }, [pagination.page, filterEmployee]);

  const fetchReferenceData = async () => {
    try {
      const [empRes, timeRes, craRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/time-entries`),
        axios.get(`${API}/payroll/cra-remittance`)
      ]);
      setEmployees(empRes.data);
      setTimeEntries(timeRes.data);
      setCraRemittance(craRes.data);
    } catch (error) { console.error('Failed to fetch reference data:', error); }
  };

  const fetchPayrolls = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filterEmployee) params.append('employee_id', filterEmployee);
      
      const res = await axios.get(`${API}/payroll?${params}`);
      
      // Handle both paginated and non-paginated responses
      if (res.data.data) {
        setPayrolls(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        // Fallback for non-paginated response
        setPayrolls(res.data);
        setPagination(prev => ({ ...prev, total: res.data.length, pages: 1 }));
      }
    } catch (error) { console.error('Failed to fetch payrolls:', error); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit, filterEmployee]);

  const fetchData = async () => {
    await Promise.all([fetchReferenceData(), fetchPayrolls()]);
  };

  const downloadPaystub = async (payrollId) => {
    try {
      const response = await axios.get(`${API}/payroll/${payrollId}/paystub`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paystub_${payrollId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error('Failed to download paystub:', error); }
  };

  const approveTimesheet = async (entryId) => {
    try {
      await axios.patch(`${API}/time-entries/${entryId}`, { status: 'approved' });
      setTimeEntries(timeEntries.map(e => e.id === entryId ? { ...e, status: 'approved' } : e));
    } catch (error) { console.error('Failed to approve timesheet:', error); }
  };

  const approveAllTimesheets = async () => {
    const pending = timeEntries.filter(e => e.status === 'pending');
    for (const entry of pending) {
      await approveTimesheet(entry.id);
    }
    fetchReferenceData();
  };

  const generateROE = (employee) => {
    setSelectedEmployee(employee);
    setShowROEModal(true);
  };

  const downloadROE = async () => {
    if (!selectedEmployee) return;
    try {
      const response = await axios.get(`${API}/payroll/roe/${selectedEmployee.id}/pdf?reason=A`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ROE_${selectedEmployee.last_name}_${selectedEmployee.first_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowROEModal(false);
    } catch (error) {
      console.error('Failed to generate ROE:', error);
      alert('Failed to generate ROE. Please try again.');
    }
  };

  const downloadAllT4sZip = async () => {
    setDownloadingZip(true);
    try {
      const response = await axios.get(`${API}/payroll/t4/zip?year=${taxFormYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4_All_Employees_${taxFormYear}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download T4 ZIP:', error);
      alert('Failed to download T4 ZIP. Please try again.');
    } finally {
      setDownloadingZip(false);
    }
  };

  const generateTaxForm = async (type) => {
    try {
      let response;
      let filename;
      
      if (type === 'T4' && selectedEmployee) {
        response = await axios.get(`${API}/payroll/t4/${selectedEmployee.id}/pdf?year=${taxFormYear}`, { responseType: 'blob' });
        filename = `T4_${taxFormYear}_${selectedEmployee.last_name}_${selectedEmployee.first_name}.pdf`;
      } else if (type === 'T2') {
        response = await axios.get(`${API}/payroll/t2/pdf?year=${taxFormYear}`, { responseType: 'blob' });
        filename = `T2_Corporate_Summary_${taxFormYear}.pdf`;
      } else {
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowTaxFormModal(false);
    } catch (error) {
      console.error('Failed to generate tax form:', error);
      alert('Failed to generate tax form. Please try again.');
    }
  };

  const runPayroll = async () => {
    setRunningPayroll(true);
    try {
      const response = await axios.post(`${API}/payroll/run`);
      alert(`Success! ${response.data.message}\nPay Period: ${response.data.pay_period}`);
      setShowRunPayrollModal(false);
      fetchPayrolls(); // Refresh the data
    } catch (error) {
      console.error('Failed to run payroll:', error);
      alert('Failed to run payroll. Please try again.');
    } finally {
      setRunningPayroll(false);
    }
  };

  const handleFilterChange = (value) => {
    setFilterEmployee(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filtering
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Use payrolls directly since filtering is now done server-side
  const filteredPayrolls = payrolls;
  const pendingTimesheets = timeEntries.filter(e => e.status === 'pending');
  const formatCurrency = (amount) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 }).format(amount || 0);

  const totalGross = filteredPayrolls.reduce((sum, p) => sum + (p.gross_pay || 0), 0);
  const totalDeductions = filteredPayrolls.reduce((sum, p) => sum + (p.cpp_deduction || 0) + (p.ei_deduction || 0) + (p.federal_tax || 0) + (p.provincial_tax || 0), 0);
  const totalNet = filteredPayrolls.reduce((sum, p) => sum + (p.net_pay || 0), 0);

  if (loading && payrolls.length === 0) { return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>; }

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-slate-900">Payroll Management</h1><p className="text-slate-500">Process payroll, remittances, and tax forms</p></div>
        <div className="flex gap-2">
          <button onClick={() => setShowTaxFormModal(true)} className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"><FileText className="w-4 h-4" /> Tax Forms</button>
          <button onClick={() => setShowRunPayrollModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg" data-testid="run-payroll-btn"><Calculator className="w-4 h-4" /> Run Payroll</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Gross Pay</p>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalGross)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Deductions</p>
          <p className="text-2xl font-bold text-rose-600">{formatCurrency(totalDeductions)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Net Pay</p>
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalNet)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Pending Timesheets</p>
          <p className="text-2xl font-bold text-amber-600">{pendingTimesheets.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {[
          { id: 'paystubs', label: 'Pay Stubs', icon: DollarSign },
          { id: 'timesheets', label: 'Timesheet Approval', icon: Clock },
          { id: 'cra', label: 'CRA Remittance', icon: FileText },
          { id: 'roe', label: 'ROE & Tax Forms', icon: Users }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors ${activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
            {tab.id === 'timesheets' && pendingTimesheets.length > 0 && <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{pendingTimesheets.length}</span>}
          </button>
        ))}
      </div>

      {/* Pay Stubs Tab */}
      {activeTab === 'paystubs' && (
        <div className="space-y-4">
          <div className="flex gap-4">
            <select value={filterEmployee} onChange={(e) => handleFilterChange(e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
              <option value="">All Employees</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Pay Period</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Hours</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Gross</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">CPP</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">EI</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Tax</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Net Pay</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredPayrolls.length === 0 ? (
                    <tr><td colSpan="9" className="px-4 py-12 text-center text-slate-500">No payroll records found</td></tr>
                  ) : (
                  filteredPayrolls.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{p.employee_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{p.pay_period_start} - {p.pay_period_end}</td>
                      <td className="px-4 py-3 text-right text-sm text-slate-600">{p.hours_worked}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900">{formatCurrency(p.gross_pay)}</td>
                      <td className="px-4 py-3 text-right text-sm text-rose-600">{formatCurrency(p.cpp_deduction)}</td>
                      <td className="px-4 py-3 text-right text-sm text-rose-600">{formatCurrency(p.ei_deduction)}</td>
                      <td className="px-4 py-3 text-right text-sm text-rose-600">{formatCurrency((p.federal_tax || 0) + (p.provincial_tax || 0))}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-600">{formatCurrency(p.net_pay)}</td>
                      <td className="px-4 py-3 text-center"><button onClick={() => downloadPaystub(p.id)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"><Download className="w-4 h-4" /></button></td>
                    </tr>
                  ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </>
            )}
          </div>
        </div>
      )}

      {/* Timesheet Approval Tab */}
      {activeTab === 'timesheets' && (
        <div className="space-y-4">
          {pendingTimesheets.length > 0 && (
            <div className="flex justify-end">
              <button onClick={approveAllTimesheets} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4" /> Approve All ({pendingTimesheets.length})</button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Hours</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {timeEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{entry.employee_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{entry.date}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{entry.start_time} - {entry.end_time}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-900">{entry.hours}</td>
                      <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">{entry.category}</span></td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{entry.description}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${entry.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{entry.status}</span></td>
                      <td className="px-4 py-3 text-center">
                        {entry.status === 'pending' && (
                          <button onClick={() => approveTimesheet(entry.id)} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700">Approve</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CRA Remittance Tab */}
      {activeTab === 'cra' && craRemittance && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
            <h3 className="text-xl font-bold mb-2">CRA Remittance Summary</h3>
            <p className="text-indigo-100">Period: {craRemittance.period}</p>
            <p className="text-3xl font-bold mt-4">{formatCurrency(craRemittance.total_remittance)}</p>
            <p className="text-indigo-100">Total Due to CRA</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h4 className="font-semibold text-slate-900 mb-4">CPP Contributions</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Employee</span><span className="font-medium">{formatCurrency(craRemittance.cpp?.employee_portion)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Employer</span><span className="font-medium">{formatCurrency(craRemittance.cpp?.employer_portion)}</span></div>
                <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Total CPP</span><span className="text-indigo-600">{formatCurrency(craRemittance.cpp?.total)}</span></div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h4 className="font-semibold text-slate-900 mb-4">EI Premiums</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Employee</span><span className="font-medium">{formatCurrency(craRemittance.ei?.employee_portion)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Employer (1.4x)</span><span className="font-medium">{formatCurrency(craRemittance.ei?.employer_portion)}</span></div>
                <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Total EI</span><span className="text-indigo-600">{formatCurrency(craRemittance.ei?.total)}</span></div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h4 className="font-semibold text-slate-900 mb-4">Income Tax</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Federal Tax</span><span className="font-medium">{formatCurrency(craRemittance.income_tax?.federal)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Provincial Tax</span><span className="font-medium">{formatCurrency(craRemittance.income_tax?.provincial)}</span></div>
                <div className="flex justify-between text-sm font-semibold border-t pt-2"><span>Total Tax</span><span className="text-indigo-600">{formatCurrency(craRemittance.income_tax?.total)}</span></div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Remittance Due Date</p>
                <p className="text-sm text-amber-700">CRA remittances are due by the 15th of the following month. Ensure timely payment to avoid penalties.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ROE & Tax Forms Tab */}
      {activeTab === 'roe' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Generate Record of Employment (ROE)</h3>
            <p className="text-sm text-slate-500 mb-4">Generate ROE for employees who are leaving or have been laid off.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employees.map(emp => (
                <div key={emp.id} className="border border-slate-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{emp.first_name} {emp.last_name}</p>
                      <p className="text-sm text-slate-500">{emp.role}</p>
                    </div>
                    <button onClick={() => generateROE(emp)} className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200">Generate ROE</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Tax Forms (T4 / T2)</h3>
                <p className="text-sm text-slate-500">Generate T4 slips for employees or T2 corporate tax summary.</p>
              </div>
              <button 
                onClick={downloadAllT4sZip} 
                disabled={downloadingZip}
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                data-testid="download-all-t4s-btn"
              >
                {downloadingZip ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                {downloadingZip ? 'Generating...' : 'Download All T4s (ZIP)'}
              </button>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-slate-700">Tax Year:</label>
              <select value={taxFormYear} onChange={(e) => setTaxFormYear(parseInt(e.target.value))} className="px-3 py-2 border border-slate-300 rounded-lg">
                {[2026, 2025, 2024, 2023].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employees.map(emp => (
                <div key={emp.id} className="border border-slate-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{emp.first_name} {emp.last_name}</p>
                      <p className="text-sm text-slate-500">{emp.role}</p>
                    </div>
                    <button onClick={() => { setSelectedEmployee(emp); setShowTaxFormModal(true); }} className="px-3 py-1 text-sm bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200">T4</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 flex gap-3">
              <button onClick={() => { setSelectedEmployee(null); generateTaxForm('T2'); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Generate T2 Corporate Summary</button>
            </div>
          </div>
        </div>
      )}

      {/* ROE Modal */}
      {showROEModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Generate ROE</h3>
              <button onClick={() => setShowROEModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <p className="text-slate-600 mb-4">Generate Record of Employment for <strong>{selectedEmployee.first_name} {selectedEmployee.last_name}</strong>?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowROEModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={downloadROE} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Download ROE</button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Form Modal */}
      {showTaxFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Generate Tax Form</h3>
              <button onClick={() => setShowTaxFormModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            {selectedEmployee ? (
              <p className="text-slate-600 mb-4">Generate T4 for <strong>{selectedEmployee.first_name} {selectedEmployee.last_name}</strong> for tax year {taxFormYear}?</p>
            ) : (
              <p className="text-slate-600 mb-4">Generate T2 Corporate Summary for tax year {taxFormYear}?</p>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowTaxFormModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={() => generateTaxForm(selectedEmployee ? 'T4' : 'T2')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Download {selectedEmployee ? 'T4' : 'T2'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Run Payroll Modal */}
      {showRunPayrollModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Run Payroll</h3>
              <button onClick={() => setShowRunPayrollModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <p className="text-slate-600 mb-4">This will process payroll for all employees based on approved timesheets. Are you sure you want to continue?</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">Pending Timesheets: <strong>{pendingTimesheets.length}</strong></p>
              {pendingTimesheets.length > 0 && <p className="text-xs text-amber-700 mt-1">You may want to approve pending timesheets before running payroll.</p>}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowRunPayrollModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={runPayroll} disabled={runningPayroll} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {runningPayroll ? 'Processing...' : 'Run Payroll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
